import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import { ThemeCustomizerButton, SiteThemeCustomizer } from '../components/desktop/ThemeCustomizer/index';
import AnimationLayer from '../components/desktop/AnimationLayer';
import { getThemeAnimations } from '@theme/animation-config';
import { Heart, MessageSquare, ThumbsDown, Repeat2, Share2, Users, Hash, Calendar, Clock, TrendingUp, TrendingDown, Flame, ChevronDown, X, MoreVertical, Edit3, Pin, Trash, Bell, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useScale, scaleW, scaleH } from '../hooks/useScale';
import { useDynamicBackground } from '../hooks/useDynamicBackground';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../utils/postUrls';
import PostDetailNavbar from '../components/desktop/PostDetailNavbar';
import { NavigateWidget } from '../components/common/widgets/NavigateWidget';
import { Post } from '../types/post';
import { normalizeLikeCounts } from '../components/shared/hooks/useFeedData';
import { canModeratePost, getEditorType, getPinnerType, User } from '../utils/permissions';
import { selectPreviewComments } from '../utils/commentPreview';
import { EditPostModal } from '../components/desktop/EditPostModal';
import { PostBadges } from '../components/desktop/PostBadges';
import { postEventBus } from '../lib/postEventBus';
import { usePostSync } from '../hooks/usePostSync';
import { calculateNavbarBg } from '../utils/backgroundHelpers';
import { getCachedTheme } from '../utils/themeCache';
import resolveAvatar from '../lib/resolveAvatar';
import { PostCreationModal } from '../components/desktop/PostCreation';

import { fetchWithAuth } from '../lib/fetchWithAuth';
export default function TagsPage() {
  const { tag } = useParams<{ tag: string }>();
  const navigate = useNavigate();
  const { theme, currentPreset, siteCustomization, siteAnimations, ensureSiteThemeApplied } = useTheme();
  const { user: authUser, loading: authLoading, logout } = useAuth();
  const { setCurrentTheme } = useNavigation();
  const scale = useScale();
  const backgroundHeight = useDynamicBackground();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [themeAnimations, setThemeAnimations] = useState(() => {
    // UNIFIED CACHE: Use animations from ThemeContext
    if (siteAnimations) {
      return siteAnimations;
    }
    // Fallback to preset animations
    return getThemeAnimations(currentPreset);
  });
  const currentUser = authUser;
  const [showSiteCustomizer, setShowSiteCustomizer] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('posts');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showPostWidget, setShowPostWidget] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [isShareAnimating, setIsShareAnimating] = useState(false);

  // Use theme from ThemeContext directly (already has computedColors applied)
  const localTheme = React.useMemo(() => {
    return theme;
  }, [theme]);

  // Calculate navbarBg, tabBarBg, and cardBg with gradient support
  const themeColors = React.useMemo(() => {
    const calculateCardBg = () => {
      if (localTheme.cardGradientEnabled && localTheme.cardGradientColor1 && localTheme.cardGradientColor2) {
        const angle = 135; // Default gradient angle
        return `linear-gradient(${angle}deg, ${localTheme.cardGradientColor1}, ${localTheme.cardGradientColor2})`;
      }
      return localTheme.cardBg;
    };

    return {
      navbarBg: calculateNavbarBg(localTheme.primaryColor!, 92),
      accentColor: localTheme.accentColor!,
      textColor: localTheme.textColor!,
      cardBg: calculateCardBg(),
      tabBarBg: localTheme.tabBarBg || 'rgba(30, 58, 95, 1)',
    };
  }, [localTheme]);


  // Register site theme with NavigationContext - ONLY when theme fully loads
  // CRITICAL: Do NOT register on mount, or it will overwrite the previous page's theme
  // before LoadingScreen can read it
  useEffect(() => {
    if (!siteCustomization) {
      // Site theme was fetched while a profile/community page was active — apply it now.
      ensureSiteThemeApplied();
      return;
    }
    if (theme?.primaryColor) {
      // Delay registration by 100ms to ensure LoadingScreen reads the previous page's theme
      const timer = setTimeout(() => {
        console.log('[TagsPage] 🎨 Setting currentTheme for loading screen (delayed):');
        setCurrentTheme({
          type: 'site',
          colors: {
            primaryColor: theme.primaryColor,
            secondaryColor: theme.secondaryColor || theme.primaryColor,
            textColor: theme.textColor || '#ffffff',
            accentColor: theme.accentColor || theme.primaryColor
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [theme?.primaryColor, theme?.secondaryColor, theme?.textColor, theme?.accentColor, siteCustomization, setCurrentTheme, ensureSiteThemeApplied]);

  // Update animations when theme changes
  useEffect(() => {
    setThemeAnimations(getThemeAnimations(currentPreset));
  }, [currentPreset]);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortBy, setSortBy] = useState<string>('newest');
  const [followedTags, setFollowedTags] = useState<string[]>([]);
  const [showModerateMenu, setShowModerateMenu] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<Post | null>(null);

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // Repost/Share modal state
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostPostId, setRepostPostId] = useState<string | null>(null);
  const [repostComment, setRepostComment] = useState('');
  const [isReposting, setIsReposting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePostId, setSharePostId] = useState<string | null>(null);

  // Draggable modal state
  const [repostModalPosition, setRepostModalPosition] = useState({ x: 0, y: 0 });
  const [isDraggingRepost, setIsDraggingRepost] = useState(false);
  const [dragStartRepost, setDragStartRepost] = useState({ x: 0, y: 0 });

  // Listen for post updates from other pages/components
  usePostSync({
    onLike: (data) => {
      setPosts(prev => prev.map(p =>
        p.id === data.postId
          ? { ...p, liked: data.liked, likes: data.likes }
          : p
      ));
    },
    onDislike: (data) => {
      setPosts(prev => prev.map(p =>
        p.id === data.postId
          ? { ...p, disliked: data.disliked, dislikes: data.dislikes }
          : p
      ));
    },
    postIds: posts.map(p => p.id)
  });

  const sortOptions = [
    { value: 'newest', label: 'Newest', icon: Calendar },
    { value: 'oldest', label: 'Oldest', icon: Clock },
    { value: 'popular', label: 'Popular', icon: TrendingUp },
    { value: 'unpopular', label: 'Unpopular', icon: TrendingDown },
    { value: 'controversial', label: 'Most Controversial', icon: Flame }
  ];

  const currentSortOption = sortOptions.find(opt => opt.value === sortBy) || sortOptions[0];

  // Repost handlers
  const handleRepost = (postId: string) => {
    setRepostPostId(postId);
    setShowRepostModal(true);
  };

  const handleSubmitRepost = async () => {
    if (!repostPostId) return;

    setIsReposting(true);
    try {
      const response = await fetchWithAuth(`/api/posts/${repostPostId}/repost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment: repostComment })
      });

      if (response.ok) {
        setShowRepostModal(false);
        setRepostComment('');
        setRepostPostId(null);
        // Optionally refresh posts or show success message
      }
    } catch (error) {
    } finally {
      setIsReposting(false);
    }
  };

  // Share handler
  const handleShare = (postId: string) => {
    setSharePostId(postId);
    setShowShareModal(true);
  };

  useEffect(() => {
    async function loadData() {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // Site theme is already loaded by ThemeContext

      // Fetch posts with this tag from API
      try {
        const response = await fetchWithAuth(`/api/tags/${tag}/posts?tab=${activeTab}`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setPosts((data.posts || []).map(normalizeLikeCounts));
        } else {
          setPosts([]);
        }
      } catch (error) {
        setPosts([]);
      }

      // Check if user is following this tag
      try {
        const response = await fetchWithAuth('/api/tags/following', {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setFollowedTags(data.tags || []);
          setIsFollowing(data.tags?.includes(tag) || false);
        }
      } catch (error) {
        setIsFollowing(false);
      }

      setLoading(false);
    }

    loadData();
  }, [tag, activeTab, authUser, authLoading]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  // Theme updates are handled automatically by ThemeContext

  const handleFollowTag = async () => {
    if (!tag) return;

    const newFollowingState = !isFollowing;
    setIsFollowing(newFollowingState);

    try {
      const response = await fetchWithAuth(`/api/tags/${tag}/follow`, {
        method: newFollowingState ? 'POST' : 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        // Revert on error
        setIsFollowing(!newFollowingState);
      }
    } catch (error) {
      // Revert on error
      setIsFollowing(!newFollowingState);
    }
  };

  const handleCreatePost = async (postData: {
    title: string;
    content: string;
    tags: string[];
    mediaUrl: string;
    mediaPosition: 'above' | 'below';
  }) => {
    setIsPosting(true);
    try {
      const postPayload = {
        title: postData.title.trim() || undefined,
        content: postData.content,
        tags: postData.tags,
        mediaUrl: postData.mediaUrl || undefined,
        mediaPosition: postData.mediaPosition,
      };

      const response = await fetchWithAuth('/api/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(postPayload)
      });

      if (response.ok) {
        const newPost = await response.json();
        // Add new post to the top of the list if it has the current tag
        if (tag && postData.tags.includes(tag)) {
          setPosts(prev => [newPost, ...prev]);
        }
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create post');
      }
    } catch (error) {
      console.error('Failed to create post:', error);
      alert('Failed to create post');
    } finally {
      setIsPosting(false);
    }
  };

  const handleToggleShareAnimation = () => {
    setIsShareAnimating(true);
    setTimeout(() => {
      setShowPostWidget(true);
      setIsShareAnimating(false);
    }, 400);
  };

  const likePost = async (postId: string) => {
    // Find the post and capture initial state
    const targetPost = posts.find(p => p.id === postId);
    if (!targetPost) return;

    const wasLiked = targetPost.liked;
    const initialLikes = targetPost.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Optimistic update
    setPosts((prev) =>
      prev.map((post) =>
        post.id === postId
          ? { ...post, liked: newLiked, likes: newLikes }
          : post
      )
    );

    // Emit local event for same-page sync
    postEventBus.emitLike(postId, newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/posts/${postId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state
        setPosts((prev) =>
          prev.map((post) =>
            post.id === postId
              ? { ...post, liked: wasLiked, likes: initialLikes }
              : post
          )
        );
        // Emit revert event
        postEventBus.emitLike(postId, wasLiked, initialLikes);
      }
    } catch (error) {
      // Revert to initial state
      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId
            ? { ...post, liked: wasLiked, likes: initialLikes }
            : post
        )
      );
      // Emit revert event
      postEventBus.emitLike(postId, wasLiked, initialLikes);
    }
  };

  const dislikePost = async (postId: string) => {
    // Find the post and capture initial state
    const targetPost = posts.find(p => p.id === postId);
    if (!targetPost) return;

    const wasDisliked = (targetPost as any).disliked || false;
    const initialDislikes = targetPost.dislikes || 0;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    // Optimistic update
    setPosts((prev) =>
      prev.map((post) =>
        post.id === postId
          ? { ...post, disliked: newDisliked, dislikes: newDislikes }
          : post
      )
    );

    // Emit local event for same-page sync
    postEventBus.emitDislike(postId, newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/posts/${postId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state
        setPosts((prev) =>
          prev.map((post) =>
            post.id === postId
              ? { ...post, disliked: wasDisliked, dislikes: initialDislikes }
              : post
          )
        );
        // Emit revert event
        postEventBus.emitDislike(postId, wasDisliked, initialDislikes);
      }
    } catch (error) {
      // Revert to initial state
      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId
            ? { ...post, disliked: wasDisliked, dislikes: initialDislikes }
            : post
        )
      );
      // Emit revert event
      postEventBus.emitDislike(postId, wasDisliked, initialDislikes);
    }
  };

  const PixelatedTurtle = ({ size = 32 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" className="pixelated">
      <g fill="#10b981">
        <rect x="12" y="8" width="8" height="8" />
        <rect x="10" y="10" width="2" height="4" />
        <rect x="20" y="10" width="2" height="4" />
        <rect x="8" y="14" width="4" height="4" />
        <rect x="20" y="14" width="4" height="4" />
        <rect x="6" y="12" width="2" height="2" />
        <rect x="24" y="12" width="2" height="2" />
        <rect x="10" y="18" width="2" height="2" />
        <rect x="20" y="18" width="2" height="2" />
        <rect x="14" y="6" width="4" height="2" />
      </g>
    </svg>
  );

  const PixelatedCoral = ({ size = 32 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" className="pixelated">
      <g fill="#f97316">
        <rect x="14" y="8" width="4" height="4" />
        <rect x="12" y="12" width="8" height="4" />
        <rect x="10" y="16" width="4" height="4" />
        <rect x="18" y="16" width="4" height="4" />
        <rect x="14" y="20" width="4" height="4" />
      </g>
    </svg>
  );

  const handleToggleFollowTag = async (tagToFollow: string, e: React.MouseEvent) => {
    e.stopPropagation();

    const isCurrentlyFollowing = followedTags.includes(tagToFollow);
    const newFollowingState = !isCurrentlyFollowing;

    // Optimistically update UI
    if (newFollowingState) {
      setFollowedTags(prev => [...prev, tagToFollow]);
    } else {
      setFollowedTags(prev => prev.filter(t => t !== tagToFollow));
    }

    try {
      const response = await fetchWithAuth(`/api/tags/${tagToFollow}/follow`, {
        method: newFollowingState ? 'POST' : 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        // Revert on error
        if (newFollowingState) {
          setFollowedTags(prev => prev.filter(t => t !== tagToFollow));
        } else {
          setFollowedTags(prev => [...prev, tagToFollow]);
        }
      }
    } catch (error) {
      // Revert on error
      if (newFollowingState) {
        setFollowedTags(prev => prev.filter(t => t !== tagToFollow));
      } else {
        setFollowedTags(prev => [...prev, tagToFollow]);
      }
    }
  };

  const handleEditPost = (post: Post) => {
    setEditingPost(post);
    setShowModerateMenu(null);
  };

  const handleSaveEdit = async (updatedData: { title?: string; content: string; mediaUrl?: string; removeMedia?: boolean }) => {
    if (!editingPost) return;

    const editorType = getEditorType(editingPost, currentUser!);
    const endpoint = editingPost.postType === 'community'
      ? `/api/moderation/communities/${editingPost.communityId}/posts/${editingPost.id}`
      : `/api/moderation/posts/${editingPost.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...updatedData,
          moderatorEditReason: editorType !== 'user' ? 'Post edited by moderator' : undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Update post in state
        const mappedEditorType = editorType === 'moderator' ? 'mod' : editorType === 'community_moderator' ? 'community_mod' : editorType;
        setPosts(prev => prev.map(p => p.id === editingPost.id ? {
          ...p,
          ...updatedData,
          editedBy: currentUser!.id.toString(),
          editedByType: mappedEditorType as 'user' | 'mod' | 'community_mod' | 'admin',
          editedAt: new Date().toISOString()
        } : p));
        setEditingPost(null);
      } else {
        throw new Error('Failed to update post');
      }
    } catch (error) {
      throw error;
    }
  };

  const handlePinPost = async (post: Post) => {
    const pinnerType = getPinnerType(post, currentUser!);
    const endpoint = post.postType === 'community'
      ? `/api/moderation/communities/${post.communityId}/posts/${post.id}/pin`
      : `/api/moderation/posts/${post.id}/pin`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Update post in state
        const mappedPinnerType = pinnerType === 'moderator' ? 'mod' : pinnerType === 'community_moderator' ? 'community_mod' : pinnerType;
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinned: data.pinned,
          pinnedBy: data.pinned ? currentUser!.id.toString() : undefined,
          pinnedByType: data.pinned ? mappedPinnerType as 'user' | 'mod' | 'community_mod' | 'admin' : undefined,
          pinnedAt: data.pinned ? new Date().toISOString() : undefined
        } : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  };

  const handlePinToFeed = async (post: Post) => {
    if (!(currentUser as any)?.isSuperAdmin) return;

    try {
      const response = await fetchWithAuth(`/api/moderation/posts/${post.id}/pin-to-feed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinnedToFeed: data.pinnedToFeed,
          pinnedToFeedAt: data.pinnedToFeed ? new Date().toISOString() : undefined
        } : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  };

  const handleDeletePost = async (post: Post) => {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }

    const endpoint = post.postType === 'community'
      ? `/api/moderation/communities/${post.communityId}/posts/${post.id}`
      : `/api/moderation/posts/${post.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // Remove post from state
        setPosts(prev => prev.filter(p => p.id !== post.id));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  };

  const handleLogout = async () => {
    logout();
  };

  const getPostUrl = (post: Post): string => {
    if (post.postType === 'forum' && post.communitySlug && post.slug) {
      return getForumPostUrl(post.communitySlug, post.slug);
    }
    if (post.postType === 'community' && post.communitySlug && post.slug) {
      return getCommunityPostUrl(post.communitySlug, post.slug);
    }
    if (post.slug && post.authorUsername) {
      return getLegacyPostUrl(post.authorUsername, post.slug);
    }
    // Fallback to legacy URL format
    return post.authorUsername ? `/u/${post.authorUsername}` : '/feed';
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        if (notification.actorId) {
          navigate(`/user/${notification.actorId}`);
        }
        break;
      case 'REPLY':
        if (notification.postId) {
          navigate(`/posts/${notification.postId}`);
        }
        break;
      case 'COMMUNITY_REPLY':
        if (notification.communityId && notification.postId) {
          navigate(`/community/${notification.communityId}/posts/${notification.postId}`);
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
    }
  };

  return (
    <div
      className="relative w-full overflow-x-hidden"
      style={{
        background: theme.appBg,
        color: localTheme.textColor,
        minHeight: backgroundHeight
      }}
    >
      <AnimationLayer animations={themeAnimations} isMobile={false} bubbleColor={localTheme.accentColor} />

      <PostDetailNavbar
        onBack={() => navigate(-1)}
        onLogout={handleLogout}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        activeTab={activeTab as 'all' | 'following' | 'friends'}
        onTabChange={(tab) => setActiveTab(tab)}
        theme={localTheme}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
      />

      {/* Page Header with Tag Title, Post Counter, and Follow Button */}
      <div style={{ paddingTop: '1.5rem', paddingBottom: '3rem' }}>
        <div className="flex items-center justify-center gap-4">
          {/* Post Counter - Left */}
          <span className="text-base font-medium" style={{ color: localTheme.textColor + 'cc' }}>
            {posts.length} post{posts.length !== 1 ? 's' : ''} on
          </span>

          {/* Tag Title - Center */}
          <h1 className="text-5xl font-bold" style={{ color: localTheme.textColor }}>
            #{tag}
          </h1>

          {/* Action Buttons - Right */}
          <div className="flex gap-3">
            {currentUser && (
              <button
                onClick={handleToggleShareAnimation}
                className="px-8 py-3 rounded-lg font-semibold text-lg transition-all overflow-hidden"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor,
                  transform: isShareAnimating ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.3s ease'
                }}
              >
                {isShareAnimating ? 'Opening...' : 'Share'}
              </button>
            )}
            <button
              onClick={handleFollowTag}
              className="px-8 py-3 rounded-lg font-semibold text-lg transition-all hover:opacity-80"
              style={{
                backgroundColor: isFollowing ? 'rgba(255, 255, 255, 0.1)' : localTheme.accentColor,
                color: localTheme.textColor,
                border: isFollowing ? `1px solid ${localTheme.accentColor}` : 'none'
              }}
            >
              {isFollowing ? 'Following' : 'Follow Tag'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Layout - Same structure as FeedClient */}
      <div className="mx-auto pb-8 w-full relative">
        {/* Main Feed Column - Centered under #:tag title */}
        <div className="w-[32vw] max-w-[900px] min-w-[500px] flex flex-col items-center" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            {/* Feed Tabs Card - Same style as FeedClient */}
            <div className="w-full rounded-lg py-5 px-8 backdrop-blur-sm border relative" style={{
              background: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
              borderColor: localTheme.accentColor + '40',
              zIndex: 1000,
              marginBottom: '1.5rem'
            }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4" style={{ marginLeft: '3rem' }}>
                  {[
                    { id: 'posts', label: 'Posts' },
                    { id: 'community', label: 'Community' },
                    { id: 'media', label: 'Media' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`text-lg font-semibold pb-1 transition-all duration-300 ${
                        activeTab === tab.id
                          ? 'text-white border-b-2 border-[#0891b2]'
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Sort Dropdown */}
                <div className="relative" style={{ zIndex: 1000 }}>
                  <button
                    onClick={() => setShowSortMenu(!showSortMenu)}
                    className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-all"
                    style={{
                      backgroundColor: localTheme.cardBg,
                      borderColor: localTheme.accentColor + '60',
                      color: localTheme.textColor
                    }}
                  >
                    <currentSortOption.icon size={16} />
                    <span>{currentSortOption.label}</span>
                    <ChevronDown size={16} className={showSortMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
                  </button>

                  {showSortMenu && (
                    <>
                      <div
                        className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[160px]"
                        style={{
                          backgroundColor: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                          borderColor: localTheme.accentColor + '60',
                          border: '1px solid',
                          zIndex: 1001
                        }}
                      >
                        {sortOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSortBy(option.value);
                              setShowSortMenu(false);
                            }}
                            className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                            style={{
                              color: option.value === sortBy ? localTheme.accentColor : localTheme.textColor,
                              backgroundColor: option.value === sortBy ? localTheme.accentColor + '20' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (option.value !== sortBy) {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '10';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (option.value !== sortBy) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <option.icon size={16} />
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 90 }}
                        onClick={() => setShowSortMenu(false)}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Wavy Divider */}
            <div className="w-full" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
              <svg width="100%" height="20" viewBox="0 0 1000 20" preserveAspectRatio="none" style={{ display: 'block' }}>
                <path
                  d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10 T450,10 T500,10 T550,10 T600,10 T650,10 T700,10 T750,10 T800,10 T850,10 T900,10 T950,10 T1000,10"
                  fill="none"
                  stroke={localTheme.accentColor}
                  strokeWidth="2"
                  opacity="0.5"
                />
              </svg>
            </div>

            {/* Posts - Same spacing as FeedClient */}
            {loading ? (
              <div className="text-center py-12" style={{ color: localTheme.textColor + '99' }}>
                Loading posts...
              </div>
            ) : (() => {
              // Backend now handles all filtering based on tab parameter
              let filteredPosts = posts;

              // Sort posts based on sortBy
              switch(sortBy) {
                case 'newest':
                  filteredPosts = [...filteredPosts].sort((a, b) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                  );
                  break;
                case 'oldest':
                  filteredPosts = [...filteredPosts].sort((a, b) =>
                    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                  );
                  break;
                case 'popular':
                  filteredPosts = [...filteredPosts].sort((a, b) => b.likes - a.likes);
                  break;
                case 'unpopular':
                  filteredPosts = [...filteredPosts].sort((a, b) =>
                    (b.dislikes || 0) - (a.dislikes || 0)
                  );
                  break;
                case 'controversial':
                  filteredPosts = [...filteredPosts].sort((a, b) => {
                    const aControversy = Math.min(a.likes, a.dislikes || 0);
                    const bControversy = Math.min(b.likes, b.dislikes || 0);
                    return bControversy - aControversy;
                  });
                  break;
                default:
                  break;
              }

              return filteredPosts.length === 0 ? (
                <div className="text-center py-12" style={{ color: localTheme.textColor + '99' }}>
                  No posts found for this filter
                </div>
              ) : (
                filteredPosts.map((post) => (
                  <div
                    key={post.id}
                    className="w-full rounded-lg p-6 backdrop-blur-sm border transition-all hover:shadow-lg relative"
                    style={{
                      background: themeColors.cardBg,
                      borderColor: localTheme.accentColor + '40',
                      marginBottom: '2rem'
                    }}
                  >
                    {/* Post Badges */}
                    <PostBadges post={post} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

                    {/* Moderate Dropdown */}
                    {canModeratePost(post, currentUser) && (
                      <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                        <button
                          onClick={() => setShowModerateMenu(showModerateMenu === post.id ? null : post.id)}
                          className="p-1.5 rounded-lg transition-all hover:opacity-80"
                          style={{
                            backgroundColor: localTheme.accentColor + '20',
                            color: localTheme.textColor
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>

                        {showModerateMenu === post.id && (
                          <>
                            <div
                              className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                              style={{
                                backgroundColor: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                                borderColor: localTheme.accentColor + '60',
                                border: '1px solid',
                                zIndex: 1001
                              }}
                            >
                              <button
                                onClick={() => handleEditPost(post)}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{
                                  color: localTheme.textColor,
                                  backgroundColor: 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <Edit3 size={16} />
                                Edit
                              </button>
                              <button
                                onClick={() => handlePinPost(post)}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{
                                  color: localTheme.textColor,
                                  backgroundColor: 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <Pin size={16} />
                                {post.isPinned ? 'Unpin' : 'Pin'}
                              </button>
                              {(currentUser as any)?.isSuperAdmin && (
                                <button
                                  onClick={() => handlePinToFeed(post)}
                                  className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                  style={{ color: localTheme.textColor, backgroundColor: 'transparent' }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <Pin size={16} />
                                  {(post as any).isPinnedToFeed ? 'Unpin from Feed' : 'Pin to Feed'}
                                </button>
                              )}
                              <button
                                onClick={() => handleDeletePost(post)}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{
                                  color: '#ef4444',
                                  backgroundColor: 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#ef444420';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <Trash size={16} />
                                Delete
                              </button>
                            </div>
                            <div
                              className="fixed inset-0"
                              style={{ zIndex: 90 }}
                              onClick={() => setShowModerateMenu(null)}
                            />
                          </>
                        )}
                      </div>
                    )}
                    {/* Post Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <Link
                        to={post.authorUsername ? `/user/${post.authorUsername}` : '#'}
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80"
                        style={{
                          background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                        }}
                      >
                        {post.authorAvatar ? (
                          <img src={post.authorAvatar} alt={post.author} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <PixelatedTurtle size={20} />
                        )}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col gap-0.5 mb-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold truncate" style={{ color: localTheme.textColor }}>{post.author}</h3>
                            <span className="text-xs flex-shrink-0" style={{ color: localTheme.textColor + '99' }}>{post.timestamp}</span>
                          </div>
                          {post.authorUsername && (
                            <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>@{post.authorUsername}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Media above content */}
                    {post.aboveContent && (post.mediaUrl || post.imageUrl) && (
                      <div className="mb-3 rounded-lg overflow-hidden">
                        <img
                          src={post.mediaUrl || post.imageUrl}
                          alt="Post media"
                          className="w-full h-auto cursor-context-menu"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                          onClick={(e) => e.preventDefault()}
                          onContextMenu={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}

                    {/* Post Content */}
                    <Link
                      to={getPostUrl(post)}
                      state={{
                        from: 'tags',
                        themeContext: {
                          type: 'site',
                          colors: {
                            primaryColor: localTheme.primaryColor,
                            secondaryColor: localTheme.secondaryColor,
                            textColor: localTheme.textColor,
                            accentColor: localTheme.accentColor
                          }
                        }
                      }}
                      className="block hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      {post.title && (
                        <h2 className="text-base font-bold mb-2" style={{ color: localTheme.textColor }}>
                          {post.title}
                        </h2>
                      )}
                      <p className="text-sm leading-relaxed mb-3 whitespace-pre-wrap" style={{ color: localTheme.textColor }}>
                        {post.content}
                      </p>
                    </Link>

                    {/* Media below content */}
                    {post.belowContent && (post.mediaUrl || post.imageUrl) && (
                      <div className="mt-3 rounded-lg overflow-hidden">
                        <img
                          src={post.mediaUrl || post.imageUrl}
                          alt="Post media"
                          className="w-full h-auto cursor-context-menu"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                          onClick={(e) => e.preventDefault()}
                          onContextMenu={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}

                    {/* Post Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.map((t, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs cursor-pointer hover:opacity-80 transition-opacity"
                            style={{
                              backgroundColor: localTheme.accentColor + '30',
                              color: localTheme.textColor
                            }}
                          >
                            #{t}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Post Actions */}
                    <div className="flex items-center gap-6 pt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <button
                        onClick={() => likePost(post.id)}
                        className="flex items-center gap-2 transition-colors"
                        style={{ color: post.liked ? '#f87171' : localTheme.textColor + '99' }}
                      >
                        <Heart size={18} className={post.liked ? "fill-current" : ""} />
                        <span className="text-sm">{post.likes}</span>
                      </button>
                      <button
                        onClick={() => dislikePost(post.id)}
                        className="flex items-center gap-2 transition-colors"
                        style={{ color: (post as any).disliked ? '#f97316' : localTheme.textColor + '99' }}
                      >
                        <ThumbsDown size={18} className={(post as any).disliked ? "fill-current" : ""} />
                        <span className="text-sm">{post.dislikes || 0}</span>
                      </button>
                      <button
                        onClick={() => navigate(getPostUrl(post))}
                        className="flex items-center gap-2 transition-colors hover:text-purple-400"
                        style={{ color: localTheme.textColor + '99' }}
                      >
                        <MessageSquare size={18} />
                        <span className="text-sm">{post.comments}</span>
                      </button>
                      <button
                        onClick={() => handleRepost(post.id)}
                        className="flex items-center gap-2 transition-colors hover:text-green-400"
                        style={{ color: localTheme.textColor + '99' }}
                      >
                        <Repeat2 size={18} />
                        <span className="text-sm">{post.shares}</span>
                      </button>
                      <button
                        onClick={() => handleShare(post.id)}
                        className="flex items-center gap-2 transition-colors hover:text-blue-400"
                        style={{ color: localTheme.textColor + '99' }}
                      >
                        <Share2 size={18} />
                      </button>
                    </div>

                    {/* Comment Preview - Max 3 comments */}
                    {post.replies && post.replies.length > 0 && (() => {
                      const previewComments = selectPreviewComments(post.replies);
                      return (
                        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                          <div className="space-y-3">
                            {previewComments.map((reply) => (
                              <div key={reply.id} className="flex gap-3">
                                <Link
                                  to={`/user/${reply.author.username}`}
                                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                  }}
                                >
                                  {reply.author.profilePicture ? (
                                    <img
                                      src={resolveAvatar(reply.author.profilePicture) || reply.author.profilePicture}
                                      alt={reply.author.username}
                                      className="w-full h-full rounded-full object-cover"
                                    />
                                  ) : (
                                    <PixelatedTurtle size={16} />
                                  )}
                                </Link>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Link
                                      to={`/user/${reply.author.username}`}
                                      className="text-xs font-semibold hover:opacity-80"
                                      style={{ color: localTheme.textColor }}
                                    >
                                      {reply.author.profileName || reply.author.username}
                                    </Link>
                                    <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>
                                      {new Date(reply.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <p className="text-xs" style={{ color: localTheme.textColor }}>{reply.content}</p>
                                  <div className="flex items-center gap-3 mt-1">
                                    <div className="flex items-center gap-1" style={{ color: localTheme.textColor + '99' }}>
                                      <Heart size={12} />
                                      <span className="text-xs">{reply.likes}</span>
                                    </div>
                                    <div className="flex items-center gap-1" style={{ color: localTheme.textColor + '99' }}>
                                      <ThumbsDown size={12} />
                                      <span className="text-xs">{reply.dislikes}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {post.comments > 3 && (
                            <div className="mt-2">
                              <button
                                onClick={() => navigate(getPostUrl(post))}
                                className="text-xs font-semibold hover:opacity-80 transition-opacity"
                                style={{ color: localTheme.accentColor }}
                              >
                                View all {post.comments} comments
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ))
              );
            })()}
          </div>

        {/* Right Sidebar with Navigate Widget and Mutual Friends - Same as FeedClient */}
        <div className="flex gap-6 absolute" style={{
          right: '2rem',
          top: 0
        }}>
            {/* Navigate Widget */}
            <div style={{ width: `${scaleW(288, scale.w)}px` }}>
              <div className="sticky" style={{ top: `${scaleH(80, scale.h)}px` }}>
                <NavigateWidget
                  theme={localTheme}
                  placeholder="Navigate..."
                  onNavigate={(path) => navigate(path)}
                />
              </div>
            </div>

            {/* Mutual Friends */}
            <div style={{ width: `${scaleW(320, scale.w)}px` }}>
              <div className="rounded-lg p-5 backdrop-blur-sm border sticky" style={{
                top: `${scaleH(80, scale.h)}px`,
                background: themeColors.cardBg,
                borderColor: localTheme.accentColor + '40',
                minHeight: `${scaleH(400, scale.h)}px`
              }}>
                <h3 className="text-lg font-bold mb-4 text-center" style={{ color: localTheme.textColor }}>Mutual Friends</h3>
                <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
                  {/* Empty state - no mutual friends yet */}
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Users size={48} className="mb-3" style={{ color: localTheme.textColor + '40' }} />
                    <p className="text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                      No mutual friends yet
                    </p>
                    <p className="text-xs px-4" style={{ color: localTheme.textColor + '66' }}>
                      Invite your friends to the platform
                    </p>
                  </div>
                  {/* When there are friends, they will appear here in a scrollable list */}
                </div>
              </div>
            </div>
        </div>
      </div>

      {/* Site Theme Customizer Button */}
      <ThemeCustomizerButton
        onClick={() => setShowSiteCustomizer(!showSiteCustomizer)}
        tooltipText="Customize Site Theme"
        accentColor={localTheme.accentColor}
        textColor={localTheme.textColor}
        show={true}
      />

      {/* Site Theme Customizer Modal */}
      <SiteThemeCustomizer
        isOpen={showSiteCustomizer}
        onClose={() => setShowSiteCustomizer(false)}
        userId={currentUser?.id.toString() || ''}
        initialAnimations={themeAnimations}
      />

      {/* Repost Modal */}
      {showRepostModal && (
        <div
          className="fixed z-[100] max-w-md w-full mx-4"
          style={{
            left: '50%',
            top: '5rem',
            transform: `translate(calc(-50% + ${repostModalPosition.x}px), ${repostModalPosition.y}px)`,
            cursor: isDraggingRepost ? 'grabbing' : 'default'
          }}
          onMouseMove={(e) => {
            if (isDraggingRepost) {
              const deltaX = e.clientX - dragStartRepost.x;
              const deltaY = e.clientY - dragStartRepost.y;
              setRepostModalPosition({ x: repostModalPosition.x + deltaX, y: repostModalPosition.y + deltaY });
              setDragStartRepost({ x: e.clientX, y: e.clientY });
            }
          }}
          onMouseUp={() => setIsDraggingRepost(false)}
          onMouseLeave={() => setIsDraggingRepost(false)}
        >
          <div className="rounded-lg backdrop-blur-sm border shadow-2xl" style={{
            background: themeColors.cardBg.replace(/[\d.]+\)$/, '0.95)'),
            borderColor: localTheme.accentColor + '40'
          }}>
            <div
              className="flex items-center justify-between mb-4 p-6 pb-2 cursor-grab active:cursor-grabbing"
              onMouseDown={(e) => {
                setIsDraggingRepost(true);
                setDragStartRepost({ x: e.clientX, y: e.clientY });
              }}
              style={{
                borderBottom: `1px solid ${localTheme.accentColor}20`,
                userSelect: 'none'
              }}
            >
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Repost</h3>
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostModalPosition({ x: 0, y: 0 });
                }}
                className="transition-colors hover:opacity-70"
                style={{ color: localTheme.textColor }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <X size={24} />
              </button>
            </div>
            <div className="px-6 pb-6">
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Add an optional comment to your repost:</p>
            <textarea
              value={repostComment}
              onChange={(e) => setRepostComment(e.target.value)}
              placeholder="What do you think about this?"
              className="w-full p-3 rounded-lg outline-none resize-none transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
              rows={4}
              maxLength={280}
            />
            <div className="text-right text-sm mb-4" style={{ color: localTheme.textColor + '99' }}>{repostComment.length}/280</div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostComment('');
                }}
                className="flex-1 py-2 rounded-lg transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRepost}
                disabled={isReposting}
                className="flex-1 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                {isReposting ? 'Reposting...' : 'Repost'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] max-w-md w-full mx-4">
          <div className="rounded-lg p-6 backdrop-blur-sm border shadow-2xl" style={{
            background: themeColors.cardBg.replace(/[\d.]+\)$/, '0.95)'),
            borderColor: localTheme.accentColor + '40'
          }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Share Post</h3>
              <button onClick={() => setShowShareModal(false)} className="transition-colors hover:opacity-70" style={{ color: localTheme.textColor }}>
                <X size={24} />
              </button>
            </div>
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Share this post:</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={async () => {
                  const post = posts.find(p => p.id === sharePostId);
                  if (post) {
                    const url = post.slug && post.authorUsername
                      ? `${window.location.origin}/profile/${post.authorUsername}/${post.slug}`
                      : `${window.location.origin}/posts/${sharePostId}`;

                    try {
                      await navigator.clipboard.writeText(url);

                      // Increment link share count
                      await fetchWithAuth(`/api/posts/${sharePostId}/link-share`, {
                        method: 'POST',
                        credentials: 'include'
                      });

                      alert('Link copied to clipboard!');
                      setShowShareModal(false);
                    } catch (err) {
                      alert('Failed to copy link');
                    }
                  }
                }}
                className="py-2 px-4 rounded-lg transition-all"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                Copy Link
              </button>
              <button
                onClick={() => setShowShareModal(false)}
                className="py-2 px-4 rounded-lg transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Post Creation Modal */}
      <PostCreationModal
        isOpen={showPostWidget}
        onClose={() => setShowPostWidget(false)}
        onSubmit={handleCreatePost}
        currentUser={currentUser ? {
          id: String(currentUser.id),
          username: currentUser.username,
          displayName: currentUser.profileName || currentUser.name || undefined,
          avatar: currentUser.profilePicture || currentUser.image || undefined,
        } : null}
        theme={localTheme}
        scale={scale}
        scaleW={scaleW}
        scaleH={scaleH}
        getCardBackground={(theme: any, customization: any, fallback: string) => fallback}
        customization={siteCustomization}
      />
    </div>
  );
}
